/**
 * Model Lab types, task metadata and scoring helpers (pure).
 *
 * Mirrors apps/ghl-middleware/src/model-lab — the middleware writes runs,
 * cases and results; the Builder reads them under admin RLS and writes labels.
 */

export const TASKS = ['analysis', 'grace', 'gate', 'sms'] as const
export type Task = (typeof TASKS)[number]

export const PROVIDER_IDS = ['anthropic', 'openai', 'deepseek', 'glm'] as const
export type ProviderId = (typeof PROVIDER_IDS)[number]

export type EffortLevel = 'low' | 'medium' | 'high'
export type RunStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled'
export type ResultStatus = 'ok' | 'parse_error' | 'api_error' | 'timeout'

export interface Contestant {
  readonly provider: ProviderId
  readonly model: string
  readonly effort?: EffortLevel
  readonly thinking?: boolean
  readonly jsonMode: boolean
}

export interface ModelPrice {
  readonly inputPerMillion: number
  readonly outputPerMillion: number
}

export interface ProviderInfo {
  readonly id: ProviderId
  readonly label: string
  readonly configured: boolean
  readonly keyEnvVar: string
  readonly redactPii: boolean
  readonly supportsThinkingToggle: boolean
  /** Catalog, most capable first; the first entry is the default for a new row. */
  readonly models: ReadonlyArray<CatalogModel>
}

export interface CatalogModel {
  readonly id: string
  readonly label: string
  readonly price: ModelPrice | null
}

/** What the live middleware runs today (CLAUDE_MODEL / CLAUDE_FOLLOWUP_MODEL). */
export interface ProductionModels {
  readonly analysis: string | null
  readonly sms: string | null
}

export interface CaseFilter {
  readonly from?: string
  readonly to?: string
  readonly samplePerTask: number
  readonly stratify: boolean
  readonly labeledOnly: boolean
  readonly reuseRunId?: string
}

export interface ModelLabRun {
  readonly id: string
  readonly location_id: string
  readonly tasks: Task[]
  readonly contestants: Contestant[]
  readonly case_filter: Partial<CaseFilter>
  readonly status: RunStatus
  readonly total_calls: number
  readonly error: string | null
  readonly created_at: string
  readonly started_at: string | null
  readonly completed_at: string | null
}

export interface ModelLabCaseSummary {
  readonly id: string
  readonly task: Task
  readonly case_key: string
  readonly location_id: string
  readonly production_output: ({ decision: string | null } & Record<string, unknown>) | null
  readonly source_created_at: string | null
}

export interface ModelLabCaseDetail extends ModelLabCaseSummary {
  readonly input_snapshot: Record<string, unknown>
}

export interface ModelLabResultSummary {
  readonly id: string
  readonly case_id: string
  readonly contestant_key: string
  readonly provider: ProviderId
  readonly model: string
  readonly status: ResultStatus
  readonly decision: string | null
  readonly input_tokens: number | null
  readonly output_tokens: number | null
  readonly latency_ms: number | null
  readonly cost_usd: number | null
  readonly error: string | null
}

export interface ModelLabResultDetail extends ModelLabResultSummary {
  readonly raw_text: string | null
  readonly parsed: Record<string, unknown> | null
}

/** analysis/grace/gate: {decision, price?}; sms: {ratings}. */
export interface LabelValue {
  readonly decision?: string
  readonly price?: number | null
  readonly ratings?: Readonly<Record<string, 'good' | 'bad'>>
}

export interface ModelLabLabel {
  readonly id: string
  readonly task: Task
  readonly case_key: string
  readonly location_id: string
  readonly label: LabelValue
  readonly notes: string | null
  readonly updated_at: string
}

// ---------------------------------------------------------------------------
// Task metadata
// ---------------------------------------------------------------------------

export const TASK_META: Readonly<Record<Task, { label: string; description: string; decisions: readonly string[] }>> = {
  analysis: {
    label: 'Booking analysis',
    description: 'Booked / declined / update fields / no action, plus price and field extraction.',
    decisions: ['MOVE_TO_BOOKED', 'UPDATE_FIELDS_ONLY', 'MARK_LOST', 'NO_ACTION'],
  },
  grace: {
    label: 'Mark-lost re-check',
    description: 'At the end of the grace period: keep the deal open, or let the lost status fire.',
    decisions: ['keep_open', 'mark_lost'],
  },
  gate: {
    label: 'Follow-up gate',
    description: 'For each due follow-up: send it, or skip (cancel) it.',
    decisions: ['send', 'skip'],
  },
  sms: {
    label: 'Follow-up SMS',
    description: 'The follow-up text itself. Rated good/bad per model.',
    decisions: [],
  },
}

export const DECISION_LABELS: Readonly<Record<string, string>> = {
  MOVE_TO_BOOKED: 'Booked',
  UPDATE_FIELDS_ONLY: 'Update fields',
  MARK_LOST: 'Lost',
  NO_ACTION: 'No action',
  keep_open: 'Keep open',
  mark_lost: 'Mark lost',
  send: 'Send',
  skip: 'Skip',
}

export function formatDecision(decision: string | null | undefined): string {
  if (!decision) return '—'
  return decision
    .split('|')
    .map((d) => DECISION_LABELS[d] ?? d)
    .join(' · ')
}

/** Must match contestantKey() in the middleware. */
export function contestantKey(c: Contestant): string {
  const parts: string[] = [c.provider, c.model]
  if (c.effort) parts.push(`effort=${c.effort}`)
  if (c.thinking !== undefined) parts.push(`thinking=${c.thinking ? 'on' : 'off'}`)
  if (c.provider !== 'anthropic' && c.jsonMode === false) parts.push('json=off')
  return parts.join(':')
}

const OPTION_RE = /^(effort|thinking|json)=/

/** "openai:ft:gpt-5.5:acme:effort=low" → "ft:gpt-5.5:acme (effort=low)". Model ids may contain colons. */
export function contestantLabel(key: string): string {
  const [, ...rest] = key.split(':')
  const opts = rest.filter((p) => OPTION_RE.test(p))
  const model = rest.filter((p) => !OPTION_RE.test(p)).join(':')
  return opts.length > 0 ? `${model} (${opts.join(', ')})` : model
}

// ---------------------------------------------------------------------------
// Contestant defaults
// ---------------------------------------------------------------------------

/** Production runs Claude at effort "low", so an Anthropic row starts there. */
export function defaultContestant(provider: ProviderInfo): Contestant {
  return {
    provider: provider.id,
    model: provider.models[0]?.id ?? '',
    effort: provider.id === 'anthropic' ? 'low' : undefined,
    jsonMode: true,
  }
}

/**
 * The next row to add: the first catalog model not already in the run,
 * preferring providers that have no row yet. Null when every configured
 * model is already used.
 */
export function nextContestant(providers: readonly ProviderInfo[], contestants: readonly Contestant[]): Contestant | null {
  const configured = providers.filter((p) => p.configured)
  const used = new Set(contestants.map((c) => `${c.provider}:${c.model}`))
  const ordered = [
    ...configured.filter((p) => !contestants.some((c) => c.provider === p.id)),
    ...configured.filter((p) => contestants.some((c) => c.provider === p.id)),
  ]
  for (const provider of ordered) {
    const model = provider.models.find((m) => !used.has(`${provider.id}:${m.id}`))
    if (model) return { ...defaultContestant(provider), model: model.id }
  }
  return null
}

export function formatPrice(price: ModelPrice | null): string {
  if (!price) return 'price unknown'
  if (price.inputPerMillion === 0 && price.outputPerMillion === 0) return 'free'
  return `$${price.inputPerMillion} / $${price.outputPerMillion} per 1M`
}

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

export type Verdict = 'correct' | 'wrong' | 'unlabeled' | 'error'

export function verdictFor(
  task: Task,
  result: Pick<ModelLabResultSummary, 'status' | 'decision' | 'contestant_key'> | undefined,
  label: LabelValue | undefined
): Verdict {
  if (!result || result.status !== 'ok') return 'error'
  if (!label) return 'unlabeled'
  if (task === 'sms') {
    const rating = label.ratings?.[result.contestant_key]
    if (!rating) return 'unlabeled'
    return rating === 'good' ? 'correct' : 'wrong'
  }
  if (!label.decision) return 'unlabeled'
  return result.decision === label.decision ? 'correct' : 'wrong'
}

/** Whether the label gives an answer this contestant can be scored against. */
function isLabelledFor(task: Task, label: LabelValue | undefined, key: string): boolean {
  if (!label) return false
  return task === 'sms' ? label.ratings?.[key] !== undefined : Boolean(label.decision)
}

/** True when the contestants' successful answers don't all agree. */
export function isDisagreement(task: Task, results: readonly ModelLabResultSummary[]): boolean {
  if (task === 'sms') return false
  const decisions = new Set(results.filter((r) => r.status === 'ok').map((r) => r.decision))
  return decisions.size > 1
}

function percentile(sorted: readonly number[], p: number): number | null {
  if (sorted.length === 0) return null
  const idx = Math.min(sorted.length - 1, Math.ceil((p / 100) * sorted.length) - 1)
  return sorted[Math.max(0, idx)]
}

export interface ScoreRow {
  readonly contestantKey: string
  readonly total: number
  readonly ok: number
  readonly parseErrors: number
  readonly apiErrors: number
  readonly labeled: number
  readonly correct: number
  /** Agreement with what production decided — reference only. */
  readonly matchesProduction: number
  readonly productionComparable: number
  readonly p50LatencyMs: number | null
  readonly p95LatencyMs: number | null
  readonly avgInputTokens: number | null
  readonly avgOutputTokens: number | null
  /** Projected USD per 1,000 decisions at list price; null when the model has no known price. */
  readonly costPer1k: number | null
  /** Estimated USD this model actually spent on this task in this run (every priced call, failed ones included). */
  readonly spentUsd: number | null
}

export function scoreTask(
  task: Task,
  contestantKeys: readonly string[],
  cases: readonly ModelLabCaseSummary[],
  results: readonly ModelLabResultSummary[],
  labels: ReadonlyMap<string, LabelValue>
): ScoreRow[] {
  const taskCases = new Map(cases.filter((c) => c.task === task).map((c) => [c.id, c]))

  return contestantKeys.map((key) => {
    const mine = results.filter((r) => r.contestant_key === key && taskCases.has(r.case_id))
    const ok = mine.filter((r) => r.status === 'ok')
    let labeled = 0
    let correct = 0
    let matchesProduction = 0
    let productionComparable = 0

    for (const r of mine) {
      const c = taskCases.get(r.case_id)
      if (!c) continue
      const label = labels.get(c.case_key)
      const verdict = verdictFor(task, r, label)
      // A failed call on a labelled case counts as wrong — otherwise a model
      // that breaks on the hard conversations would score higher.
      const failedOnLabelled = verdict === 'error' && isLabelledFor(task, label, r.contestant_key)
      if (verdict === 'correct' || verdict === 'wrong' || failedOnLabelled) labeled++
      if (verdict === 'correct') correct++
      const prod = c.production_output?.decision
      if (task !== 'sms' && prod && r.status === 'ok') {
        productionComparable++
        if (r.decision === prod) matchesProduction++
      }
    }

    const latencies = ok.map((r) => r.latency_ms ?? 0).sort((a, b) => a - b)
    const costs = ok.map((r) => r.cost_usd)
    const allPriced = costs.length > 0 && costs.every((c) => c !== null)
    const avg = (values: number[]) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null)

    return {
      contestantKey: key,
      total: mine.length,
      ok: ok.length,
      parseErrors: mine.filter((r) => r.status === 'parse_error').length,
      apiErrors: mine.filter((r) => r.status === 'api_error' || r.status === 'timeout').length,
      labeled,
      correct,
      matchesProduction,
      productionComparable,
      p50LatencyMs: percentile(latencies, 50),
      p95LatencyMs: percentile(latencies, 95),
      avgInputTokens: avg(ok.map((r) => r.input_tokens ?? 0)),
      avgOutputTokens: avg(ok.map((r) => r.output_tokens ?? 0)),
      costPer1k: allPriced ? (avg(costs as number[]) ?? 0) * 1000 : null,
      spentUsd: sumCosts(mine),
    }
  })
}

export interface RunModelSummary {
  readonly contestantKey: string
  readonly calls: number
  readonly failed: number
  /** Estimated spend per decision type; null when that type had no priced call. */
  readonly spentByTask: Readonly<Partial<Record<Task, number | null>>>
  readonly totalSpent: number | null
  readonly avgLatencyMs: number | null
}

/** One row per model across every decision type in the run. Pure. */
export function summarizeRunByModel(
  contestantKeys: readonly string[],
  tasks: readonly Task[],
  cases: readonly ModelLabCaseSummary[],
  results: readonly ModelLabResultSummary[]
): RunModelSummary[] {
  const taskOfCase = new Map(cases.map((c) => [c.id, c.task]))
  return contestantKeys.map((key) => {
    const mine = results.filter((r) => r.contestant_key === key)
    const spentByTask: Partial<Record<Task, number | null>> = {}
    for (const task of tasks) spentByTask[task] = sumCosts(mine.filter((r) => taskOfCase.get(r.case_id) === task))
    const latencies = mine.filter((r) => r.status === 'ok').map((r) => r.latency_ms ?? 0)
    return {
      contestantKey: key,
      calls: mine.length,
      failed: mine.filter((r) => r.status !== 'ok').length,
      spentByTask,
      totalSpent: sumCosts(mine),
      avgLatencyMs: latencies.length ? latencies.reduce((a, b) => a + b, 0) / latencies.length : null,
    }
  })
}

/** Sum of estimated cost; null when nothing in the set has a known price. */
export function sumCosts(results: ReadonlyArray<Pick<ModelLabResultSummary, 'cost_usd'>>): number | null {
  const priced = results.filter((r) => r.cost_usd !== null)
  return priced.length === 0 ? null : priced.reduce((sum, r) => sum + Number(r.cost_usd), 0)
}

/** Estimated spend for a whole run, total and per provider (to check against provider balances). */
export function runSpend(results: readonly ModelLabResultSummary[]): {
  readonly total: number | null
  readonly byProvider: ReadonlyArray<{ readonly provider: ProviderId; readonly usd: number }>
} {
  const byProvider = PROVIDER_IDS.map((provider) => ({ provider, usd: sumCosts(results.filter((r) => r.provider === provider)) }))
    .filter((p): p is { provider: ProviderId; usd: number } => p.usd !== null)
  return { total: sumCosts(results), byProvider }
}

export function formatPct(numerator: number, denominator: number): string {
  if (denominator === 0) return '—'
  return `${Math.round((numerator / denominator) * 100)}%`
}

export function formatMs(ms: number | null): string {
  if (ms === null) return '—'
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`
}

export function formatUsd(value: number | null): string {
  if (value === null) return '—'
  return value >= 1 ? `$${value.toFixed(2)}` : `$${value.toFixed(3)}`
}

/** Rough per-call token assumption for the pre-run cost estimate only. */
export const ESTIMATE_TOKENS_PER_CALL = { input: 4000, output: 1500 } as const
